import { useState, useEffect } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import DualLandedPrice from "./DualLandedPrice";
import {
  CHEEP_LUXURY_PLACEHOLDER_DATA_URI,
  resolveListingImage,
} from "../lib/productImageAssets";

/** Map `/cart` line item to concierge display shape. */
export function mapCartLineToConciergeItem(cartItem) {
  const landed =
    cartItem?.landed_price ?? cartItem?.calculated_landed_price ?? 0;
  return {
    item_title: cartItem?.name || cartItem?.item_title || "Concierge item",
    marketplace_source:
      cartItem?.marketplace || cartItem?.marketplace_source || "",
    calculated_landed_price: Number(landed) || 0,
    image_url: resolveListingImage(cartItem),
    variant_title: cartItem?.variant_title || null,
  };
}

export function isConciergeCartItem(cartItem) {
  if (!cartItem) return false;
  if (cartItem.pricing_model === 'dhc_matrix' || cartItem.pricing_model === 'dhc_failsafe' || cartItem.pricing_model === 'global_failsafe') return true;
  if (cartItem.ingestion_type === "concierge_import") return true;
  const title = cartItem.name || cartItem.item_title || "";
  return title.startsWith("Concierge Import:");
}

/**
 * Concierge cart row — dynamic image, marketplace label, DHC landed price.
 * Uses branded CheepSweepz placeholder when marketplace CDN blocks hotlinking.
 */
export default function ConciergeCartItem({
  item,
  onRemove,
  quantity,
  onQuantityChange,
  itemTotal,
  formatPrice,
  compact = false,
  testId,
}) {
  const fallbackImg = CHEEP_LUXURY_PLACEHOLDER_DATA_URI;
  const [imgSrc, setImgSrc] = useState(item?.image_url || fallbackImg);

  useEffect(() => {
    setImgSrc(item?.image_url || fallbackImg);
  }, [item?.image_url, fallbackImg]);

  const handleImageError = () => {
    if (imgSrc !== fallbackImg) {
      setImgSrc(fallbackImg);
    }
  };

  const price = Number(item?.calculated_landed_price) || 0;
  const showQuantity = typeof quantity === "number" && onQuantityChange;

  if (compact) {
    return (
      <div
        className="flex items-center gap-3"
        data-testid={testId || "concierge-cart-item-compact"}
      >
        <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex items-center justify-center p-0.5 flex-shrink-0 border border-slate-200">
          <img
            src={imgSrc}
            alt={item.item_title}
            className="w-full h-full object-contain"
            onError={handleImageError}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate text-slate-900">
            {item.item_title}
          </p>
          {item.marketplace_source ? (
            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
              {item.marketplace_source}
            </p>
          ) : null}
        </div>
        <div className="text-right flex-shrink-0">
          <DualLandedPrice
            amount={itemTotal ?? price}
            size="sm"
            usdClassName="text-emerald-600"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 p-4 bg-slate-900 rounded-xl border border-slate-800 mb-3 shadow-sm"
      data-testid={testId || "concierge-cart-item"}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex items-center justify-center p-1 flex-shrink-0">
            <img
              src={imgSrc}
              alt={item.item_title}
              className="w-full h-full object-contain"
              onError={handleImageError}
            />
          </div>

          <div className="min-w-0">
            <h4 className="text-white font-semibold text-sm line-clamp-2">
              {item.item_title}
            </h4>
            {item.variant_title ? (
              <p className="text-slate-400 text-xs mt-0.5">{item.variant_title}</p>
            ) : null}
            {item.marketplace_source ? (
              <p className="text-slate-400 text-xs uppercase font-bold tracking-wider mt-1">
                {item.marketplace_source}
              </p>
            ) : null}
            <div className="text-emerald-400 font-bold mt-1">
              <DualLandedPrice amount={price} size="md" usdClassName="text-emerald-400" />
              {showQuantity && quantity > 1 ? (
                <span className="text-slate-500 text-xs font-normal ml-1">each</span>
              ) : null}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="text-slate-500 hover:text-red-400 p-2 transition-colors flex-shrink-0"
          aria-label="Remove item"
          data-testid="concierge-cart-remove"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {showQuantity ? (
        <div className="flex items-center justify-between border-t border-slate-800 pt-3">
          <div className="flex items-center border border-slate-700 rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => onQuantityChange(quantity - 1)}
              className="p-2 text-slate-300 hover:bg-slate-800"
              aria-label="Decrease quantity"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="px-3 py-1 text-sm text-white border-x border-slate-700 min-w-[2.5rem] text-center">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => onQuantityChange(quantity + 1)}
              className="p-2 text-slate-300 hover:bg-slate-800"
              aria-label="Increase quantity"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {itemTotal != null ? (
            <DualLandedPrice amount={itemTotal} size="md" usdClassName="text-emerald-400" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
